import { TableSchema, Query, MaterializeOptions, TableFormat, TableDataSource, TableSourceType } from './types';
import { MaterializedTableData } from './table-data';
import { TableView } from './table-view';
import { Table } from 'apache-arrow';

/**
 * Feature flags for engine capabilities
 */
export interface EngineFeatures {
  /**
   * Whether the engine supports direct SQL queries
   */
  readonly supportsSQL: boolean;
}

/**
 * Options for table registration
 */
export interface TableRegistrationOptions {
  /**
   * Whether to validate the data against the schema
   */
  validateSchema?: boolean;

  /**
   * Path to register the table under (e.g. "examples/foo.csv")
   * If not provided, a unique ID will be generated
   */
  path?: string;

  /**
   * Custom options specific to the engine implementation
   */
  [key: string]: unknown;
}

/**
 * Source type for a table
 */
export { TableSourceType };

/**
 * Parameters for table registration
 */
export interface RegisterTableParams {
  /**
   * The data source for the table
   */
  source: TableDataSource;

  /**
   * Registration options
   */
  options?: TableRegistrationOptions;
}

/**
 * Reference to a registered table
 * @memoryUsage While reference itself is lightweight,
 * it points to potentially large table data
 */
export interface TableRef {
  /**
   * Unique identifier for the table
   * Format depends on source type:
   * - LOCAL_FILE: 'local-{uuid}'
   * - URL: 'url-{uuid}'
   * - Others: Generated UUID
   */
  id: string;

  /**
   * Path to the dataset in the format "/path/to/dataset.extension"
   * 
   * This path serves multiple purposes:
   * 1. Used in UI to display folder structure in the datasets sidebar
   * 2. Used in SQL queries to reference tables (e.g. SELECT * FROM "/path/to/dataset")
   * 3. Provides a human-readable identifier for the dataset
   * 
   * For files: Should reflect the original file path
   * For URLs: Should use a path-like structure derived from the URL
   * For others: Should use a meaningful path structure
   */
  path: string;

  /**
   * Schema of the table
   */
  schema: TableSchema;

  /**
   * Source information to recreate the table
   */
  source: TableDataSource;
}

/**
 * Callback for table changes
 */
export type TableChangeCallback = () => void;

/**
 * Export options for the exportToURL method
 */
export interface ExportToURLOptions {
  /**
   * The table view to export
   */
  view: TableView;
  
  /**
   * The signed URL to export to
   */
  signedUrl: string;
}

/**
 * Interface for query engine implementations
 * @memoryUsage Engine instances can be memory intensive:
 * - Hold references to all registered tables
 * - Maintain query execution buffers
 * - Cache intermediate results
 * Always dispose when done to free resources
 */
export interface Engine {
  /**
   * Get the feature flags for this engine
   */
  readonly features: EngineFeatures;

  /**
   * Register a table with the engine
   */
  registerTable(params: RegisterTableParams): Promise<TableRef>;

  /**
   * Register a table from a File object
   * This is a convenience method for handling file uploads in the browser
   * @param file The File object to register
   * @param options Optional registration options
   * @abstract This method must be implemented by engine implementations
   */
  registerFromFile(file: File, options?: TableRegistrationOptions): Promise<TableRef>;

  /**
   * Register a table from a URL
   * This is a convenience method for loading data from a URL
   * @param params Configuration object with URL registration parameters
   * @param params.url The URL to load data from
   * @param params.format The format of the data (CSV, JSON, etc.)
   * @param params.options Optional registration options
   * @returns Table reference
   */
  registerFromURL(params: { url: string; format: TableFormat; options?: TableRegistrationOptions }): Promise<TableRef>;

  /**
   * Get a view of a registered table
   */
  getTableView(ref: TableRef): Promise<TableView>;

  /**
   * Transform a table view using a query
   * Returns a new view that represents the transformed data
   */
  transform(view: TableView, query: Query): Promise<TableView>;

  /**
   * Remove a table from the engine
   */
  unregisterTable(ref: TableRef): Promise<void>;

  /**
   * Clean up any resources used by the engine
   */
  dispose(): Promise<void>;

  /**
   * Materialize a view into concrete data
   */
  materialize(view: TableView, options?: MaterializeOptions): Promise<MaterializedTableData>;

  /**
   * Export query results to a URL without pagination
   * @param options Export options including view and signedUrl
   * @returns Promise that resolves when the export is complete
   * @abstract This method must be implemented by engine implementations
   */
  exportToURL(options: ExportToURLOptions): Promise<void>;

  /**
   * List all registered tables
   * @returns Array of TableRef objects for all registered tables
   * @memoryUsage Lightweight operation that returns references only
   */
  listRegisteredTables(): Promise<TableRef[]>;
  
  /**
   * Register a callback to be called when tables change
   * @param callback The callback to register
   * @returns A function to unregister the callback
   */
  onTablesChange(callback: TableChangeCallback): () => void;
}

/**
 * Information about a registered table
 * @memoryUsage Metadata only, but references potentially large table
 */
interface RegisteredTable {
  /**
   * Schema of the table
   */
  schema: TableSchema;

  /**
   * User-friendly path for the table
   */
  path: string;
}

/**
 * Base implementation of the Engine interface
 * @memoryUsage Maintains map of all registered tables
 * Memory usage scales with number and size of tables
 */
export abstract class BaseEngine implements Engine {
  /**
   * Map of registered table information
   * Stores only schema and path information, not materialized data
   */
  protected readonly tables = new Map<string, RegisteredTable>();

  /**
   * Callbacks to be called when tables change
   * @private
   */
  private tableChangeCallbacks: Set<TableChangeCallback> = new Set();

  constructor(readonly features: EngineFeatures) {}

  /**
   * Register a table from a File object
   * This is a convenience method for handling file uploads in the browser
   * @param file The File object to register
   * @param options Optional registration options
   * @abstract This method must be implemented by engine implementations
   */
  registerFromFile(_file: File, _options?: TableRegistrationOptions): Promise<TableRef> {
    throw new Error('registerFromFile not implemented for this engine');
  }

  /**
   * Register a table from a URL
   * This is a convenience method for loading data from a URL
   * @param params Configuration object with URL registration parameters
   * @param params.url The URL to load data from
   * @param params.format The format of the data (CSV, JSON, etc.)
   * @param params.options Optional registration options
   * @returns Table reference
   */
  registerFromURL(params: { url: string; format: TableFormat; options?: TableRegistrationOptions }): Promise<TableRef> {
    const { url, format, options } = params;
    throw new Error(`registerFromURL not implemented for this engine (URL: ${url}, format: ${format}, options: ${options ? 'provided' : 'none'})`);
  }

  /**
   * Generate a table ID based on the source type
   */
  protected generateTableId(source: TableDataSource): string {
    const uuid = crypto.randomUUID();
    
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return `local-${uuid}`;
      case TableSourceType.URL:
        return `url-${uuid}`;
      default:
        return uuid;
    }
  }

  /**
   * Get a default path for a table based on its source
   */
  private getDefaultPath(source: TableDataSource, id: string): string {
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return source.fileId;
      case TableSourceType.URL:
        try {
          const url = new URL(source.url);
          return url.pathname.split('/').pop() || source.url;
        } catch {
          return source.url;
        }
      case TableSourceType.MATERIALIZED:
        return `table_${id}`;
    }
  }

  /**
   * Register a table with the engine
   */
  async registerTable(params: RegisterTableParams): Promise<TableRef> {
    const { source, options = {} } = params;
    
    // Get schema from the appropriate source
    const schema = this.getSchemaFromSource(source);
    
    if (options.validateSchema) {
      await this.validateTableSchema(schema);
    }
    
    // Generate or use provided path
    const id = options.path || this.generateTableId(source);
    
    // Check if a table with this ID already exists
    if (this.tables.has(id)) {
      throw new Error(`Table already exists: ${id}`);
    }

    // Store only the schema and path information
    const path = options.path || this.getDefaultPath(source, id);
    this.tables.set(id, {
      schema,
      path
    });
    
    const ref: TableRef = {
      id,
      path,
      schema,
      source
    };
    
    // Notify callbacks that tables have changed
    this.notifyTableChange();

    return ref;
  }

  /**
   * Remove a table from the engine
   */
  async unregisterTable(ref: TableRef): Promise<void> {
    // Remove the table from the registry
    this.tables.delete(ref.id);
    
    // Notify callbacks that tables have changed
    this.notifyTableChange();
  }

  abstract getTableView(ref: TableRef): Promise<TableView>;
  
  /**
   * Transform a table view using a query
   * Each engine implementation must provide its own transformation logic
   */
  abstract transform(view: TableView, query: Query): Promise<TableView>;
  
  abstract dispose(): Promise<void>;

  /**
   * Extract schema from the data source
   */
  private getSchemaFromSource(source: TableDataSource): TableSchema {
    switch (source.type) {
      case TableSourceType.LOCAL_FILE:
        return source.schema;
      case TableSourceType.URL:
        if (!source.schema) {
          throw new Error('Schema is required for URL source. Use an engine implementation that supports schema inference.');
        }
        return source.schema;
      case TableSourceType.MATERIALIZED:
        return source.data.schema;
    }
  }

  /**
   * Validate a table's schema
   */
  protected async validateTableSchema(schema: TableSchema): Promise<void> {
    // Basic validation - implementations can extend this
    if (!schema || !schema.columns || schema.columns.length === 0) {
      throw new Error('Invalid table schema');
    }
  }

  /**
   * Materialize a view into concrete data
   * Each engine implementation must provide its own materialization logic
   * @memoryUsage Creates a MaterializedTableData instance which holds all data in memory
   */
  abstract materialize(view: TableView, options?: MaterializeOptions): Promise<MaterializedTableData>;

  /**
   * Export query results to a URL without pagination
   * @param options Export options including view and signedUrl
   * @returns Promise that resolves when the export is complete
   * @abstract This method must be implemented by engine implementations
   */
  exportToURL(_options: ExportToURLOptions): Promise<void> {
    throw new Error('exportToURL not implemented for this engine');
  }

  /**
   * List all registered tables
   * @returns Array of TableRef objects for all registered tables
   */
  async listRegisteredTables(): Promise<TableRef[]> {
    const result: TableRef[] = [];
    
    for (const [id, table] of this.tables.entries()) {
      // We need to reconstruct the TableRef objects
      // Note: We don't have the original source information here
      // so we create a placeholder source of type MATERIALIZED
      // Implementations may override this method to provide complete source information
      const ref: TableRef = {
        id,
        path: table.path,
        schema: table.schema,
        source: {
          type: TableSourceType.MATERIALIZED,
          // We can't directly create MaterializedTableData with TableSchema
          // So we create a placeholder with an empty object that satisfies the interface
          data: {
            table: new Table(),
            schema: table.schema,
            rowCount: 0
          } as MaterializedTableData
        }
      };
      
      result.push(ref);
    }
    
    return result;
  }

  /**
   * Notify all registered callbacks that tables have changed
   * @protected
   */
  protected notifyTableChange(): void {
    // Call all registered callbacks
    this.tableChangeCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in table change callback:', error);
      }
    });
  }
  
  /**
   * Register a callback to be called when tables change
   * @param callback The callback to register
   * @returns A function to unregister the callback
   */
  onTablesChange(callback: TableChangeCallback): () => void {
    this.tableChangeCallbacks.add(callback);
    
    // Return a function to unregister the callback
    return () => {
      this.tableChangeCallbacks.delete(callback);
    };
  }
} 